import React, { useCallback, memo, useContext } from 'react';
import {
  Button, makeStyles, mergeClasses, tokens, useId,
} from '@fluentui/react-components';
import { ArrowCircleRight24Regular } from '@fluentui/react-icons';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';

const useSiteLinkStyles = makeStyles({
  comment: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    lineHeight: tokens.fontSizeBase600,
    marginTop: tokens.spacingVerticalSNudge,
    marginBottom: tokens.spacingVerticalNone,
    fontWeight: tokens.fontWeightRegular,
  },
  siteLinkList: {
    listStyleType: 'none',
    marginTop: tokens.spacingVerticalNone,
    marginBottom: tokens.spacingVerticalNone,
    paddingLeft: tokens.spacingHorizontalNone,
    '& li': {
      display: 'inline',
    },
  },
  singleButton: {
    marginBottom: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXS,
    textOverflow: 'ellipsis',
  },
});

export interface ISingleSiteLinkData {
  url: string;
  displayName: string;
}

type ISingleSiteLink = {
  onClickSiteLink: (
    data: ISingleSiteLinkData
  ) => void;
  data: any;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>;

export const SingleSiteLink: React.FC<ISingleSiteLink> = memo(({
  onClickSiteLink,
  data,
}) => {
  const styles = useSiteLinkStyles();

  const onClickButton = useCallback(() => {
    onClickSiteLink(data);
  }, [data, onClickSiteLink]);

  return (
    <Button
      onClick={onClickButton}
      appearance="secondary"
      size="small"
      aria-label={data?.displayName}
      className={mergeClasses(styles.singleButton, 'single-site-link')}
      icon={<ArrowCircleRight24Regular />}
      role="link"
    >
      {data?.displayName}
    </Button>
  );
});

export interface ISiteLinks {
  displayName: string;
  url: string;
}

type ISiteLinkPanel = {
  onClickSiteLink: (
    data: ISingleSiteLinkData
  ) => void;
  siteLinks: ISiteLinks[],
  hideComment?: boolean,
};

export const SiteLinkPanel: React.FC<ISiteLinkPanel> = memo((props) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const styles = useSiteLinkStyles();
  const takeMeThereId = useId('take-me-there');

  const {
    onClickSiteLink,
    siteLinks,
    hideComment,
  } = props;

  return (
    <div className="site-link-panel">
      {
        siteLinks && siteLinks.length > 0
        && (
          <>
            {!hideComment
              && (
                <h3 className={mergeClasses(styles.comment, 'comment')} id={takeMeThereId}>
                  {i18n.getString(_TL_('Take me there now:'))}
                </h3>
              )}
            <div role="group" aria-labelledby={takeMeThereId}>
              <ul className={mergeClasses(styles.siteLinkList, 'site-link-list')}>
                {
                  siteLinks && siteLinks.map((data) => (
                    <li key={data.url}>
                      <SingleSiteLink
                        key={data.url}
                        onClickSiteLink={onClickSiteLink}
                        data={data}
                      />
                    </li>
                  ))
                }
              </ul>
            </div>
          </>
        )
      }
    </div>
  );
});
